import { useAuth0 } from "@auth0/auth0-react";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import ChainRunOverlay from "./ChainRunOverlay";
import ContributionsTab from "./ContributionsTab";
import GroceryReplacementTab from "./GroceryReplacementTab";
import IngestJobsTray from "./IngestJobsTray";
import IngestTab from "./IngestTab";
import ReviewTab from "./ReviewTab";
import SearchEditTab from "./SearchEditTab";
import { BackgroundRunsProvider, useBackgroundRuns } from "./backgroundRuns";
import { isRetryJob, setIngestJobsUserScope } from "./ingestJobs";
import { setRunStoreUserScope } from "./runStore";
import { Modal, PrimaryButton, SecondaryButton, Toast } from "./shared";
import { IngestExcludedInput, IngestReviewItem } from "./types";
import {
  CatalogConfig,
  RunCancelledError,
  TOP_UP_HREF,
  chainIdConfig,
  excludedReasonText,
  resetChainIdsToEnv,
  setAltFinderChainIdOverride,
  setAutoMergeDuplicates,
  setIngestChainIdOverride,
  setMergeChainIdOverride,
  setServerCatalogConfig,
  useCatalogApi,
} from "./useCatalogApi";
import { useIngestJobs } from "./useIngestJobs";

// Feature flag (kebab-case; the LD provider keeps raw keys — see App.tsx) that
// gates the experimental "Grocery Replacement Test" tab. Create this flag in
// LaunchDarkly and target it to the accounts you want to test the tool.
const GROCERY_REPLACE_FLAG = "berg-grocery-replace-ui";
// Matches the server's default until its config (with the live average) loads.
const ROUGH_TOKENS_PER_PRODUCT = 5000;

type Mode = "add" | "manage" | "mine" | "replace";
type Step = "input" | "review";

interface CatalogManagerProps {
  customerName?: string;
}

/** A numbered step pill in the "add products" flow. */
const StepPill: React.FC<{
  index: number;
  label: string;
  hint: string;
  state: "done" | "active" | "todo";
  enabled: boolean;
  onClick: () => void;
}> = ({ index, label, hint, state, enabled, onClick }) => {
  const circle =
    state === "done"
      ? "bg-green-600 text-white"
      : state === "active"
      ? "bg-indigo-600 text-white"
      : "bg-gray-200 text-gray-500";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!enabled}
      className={`flex flex-1 items-center gap-3 rounded-lg border px-4 py-3 text-left transition ${
        state === "active"
          ? "border-indigo-300 bg-indigo-50"
          : "border-gray-200 bg-white"
      } ${enabled ? "hover:border-indigo-300" : "cursor-default opacity-80"}`}
    >
      <span
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${circle}`}
      >
        {state === "done" ? "✓" : index}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-gray-900">
          {label}
        </span>
        <span className="block truncate text-xs text-gray-500">{hint}</span>
      </span>
    </button>
  );
};

const ModeButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-md px-4 py-2 text-sm font-semibold transition ${
      active
        ? "bg-indigo-600 text-white shadow"
        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
    }`}
  >
    {children}
  </button>
);

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

const jobToastMessage = (
  t: {
    label: string;
    status: "completed" | "failed";
    resultCount?: number;
    skipped: string[];
  } | null
): string => {
  if (!t) return "";
  if (t.status === "failed")
    return `Research failed: ${t.label}. Open the ingest runs tray to retry.`;
  const found = t.resultCount || 0;
  const skipped = t.skipped.length;
  if (found > 0) {
    const skippedNote =
      skipped > 0
        ? ` ${plural(skipped, "entry", "entries")} ${
            skipped === 1 ? "was" : "were"
          } too general and skipped.`
        : "";
    return `Research complete: ${t.label} (${plural(
      found,
      "product"
    )}).${skippedNote} Nothing is saved yet — open the results and click Save.`;
  }
  if (skipped > 0) {
    const names =
      skipped === 1
        ? `“${t.skipped[0]}” is`
        : `${plural(skipped, "entry", "entries")} are`;
    return `Research finished, but ${names} too general to research, so nothing was found. Add the brand (or a UPC or link) and try again.`;
  }
  return `Research finished, but no products were found for ${t.label}. Try the brand and full product name, a UPC, or a link.`;
};

// Inputs the research run skipped (usually general foods with no brand), with
// a way to follow up on each one.
const SkippedInputsPanel: React.FC<{
  excluded: IngestExcludedInput[];
  foundCount: number;
  onRetry: (text: string) => void;
  onSearch: (text: string) => void;
}> = ({ excluded, foundCount, onRetry, onSearch }) => (
  <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4">
    <div className="text-sm font-semibold text-amber-900">
      {excluded.length === 1
        ? "1 entry wasn't researched"
        : `${excluded.length} entries weren't researched`}
      {foundCount === 0 ? ", so this run has nothing to save" : ""}
    </div>
    <p className="mt-1 text-sm text-amber-800">
      Research only works on specific products. It can&apos;t pick products for
      you from a general description. Add the brand (or use a UPC or product
      link) and research again, or check whether the product is already in the
      catalog.
      {foundCount > 0
        ? ` The ${foundCount} product${
            foundCount === 1 ? "" : "s"
          } we did find ${foundCount === 1 ? "is" : "are"} below.`
        : ""}
    </p>
    <ul className="mt-3 space-y-2">
      {excluded.map((e) => (
        <li
          key={e.raw_input}
          className="flex flex-col gap-2 rounded-md border border-amber-100 bg-white px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-gray-900">
              &ldquo;{e.raw_input}&rdquo;
            </div>
            {excludedReasonText(e.reason) && (
              <div className="text-xs text-gray-600">
                {excludedReasonText(e.reason)}
              </div>
            )}
          </div>
          <div className="flex flex-shrink-0 flex-wrap gap-2">
            <SecondaryButton
              type="button"
              onClick={() => onSearch(e.raw_input)}
            >
              Search the catalog
            </SecondaryButton>
            <PrimaryButton type="button" onClick={() => onRetry(e.raw_input)}>
              Add a brand &amp; research again
            </PrimaryButton>
          </div>
        </li>
      ))}
    </ul>
  </div>
);

const CatalogManagerInner: React.FC<CatalogManagerProps> = ({
  customerName,
}) => {
  const api = useCatalogApi();
  const { user } = useAuth0();
  const flags = useFlags();
  const backgroundRuns = useBackgroundRuns();
  const groceryReplaceEnabled = flags[GROCERY_REPLACE_FLAG] === true;
  const jobs = useIngestJobs(api);
  const [mode, setMode] = useState<Mode>("add");
  const [step, setStep] = useState<Step>("input");
  const [reviewItems, setReviewItems] = useState<IngestReviewItem[]>([]);
  // The chain execution that produced the items currently under review; stamped
  // as the origin id on write so records link back to their run on easyai.
  const [reviewExecutionId, setReviewExecutionId] = useState<
    string | undefined
  >(undefined);
  // Which background job's progress dialog is open (null = hidden/tray-only).
  const [focusedJobId, setFocusedJobId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Scope persisted jobs to the signed-in user so runs don't leak across
  // accounts on a shared machine.
  useEffect(() => {
    setIngestJobsUserScope(user?.sub);
    setRunStoreUserScope(user?.sub);
  }, [user?.sub]);

  const focusedJob = useMemo(
    () => jobs.jobs.find((j) => j.id === focusedJobId) || null,
    [jobs.jobs, focusedJobId]
  );

  // Keys already written for the run currently under review — sourced from the
  // (persisted) ingest job so the written state survives tab switches/reloads.
  const reviewWrittenKeys = useMemo(
    () =>
      jobs.jobs.find((j) => j.id === reviewExecutionId)?.writtenKeys ??
      undefined,
    [jobs.jobs, reviewExecutionId]
  );

  // Inputs the run under review skipped, plus one-shot prefills for following
  // up on them (research again with a brand, or search the catalog).
  const [reviewExcluded, setReviewExcluded] = useState<IngestExcludedInput[]>(
    []
  );
  const [ingestPrefill, setIngestPrefill] = useState<
    { text: string; nonce: number } | undefined
  >(undefined);
  const [searchPrefill, setSearchPrefill] = useState<
    { text: string; nonce: number } | undefined
  >(undefined);
  const retryWithBrand = (text: string) => {
    setIngestPrefill({ text, nonce: Date.now() });
    setStep("input");
  };
  const searchCatalogFor = (text: string) => {
    setSearchPrefill({ text, nonce: Date.now() });
    setMode("manage");
  };

  // Load a finished run's results into the review step.
  const openRunResults = (jobId: string) => {
    const job = jobs.getJob(jobId);
    if (!job?.resultItems) return;
    setReviewItems(job.resultItems);
    setReviewExcluded(job.excluded || []);
    setReviewExecutionId(job.executionId);
    setFocusedJobId(null);
    setMode("add");
    setStep("review");
  };

  // Manual "View results" click from the tray. Switching to a DIFFERENT run
  // replaces the queue currently under review; that queue isn't lost (it stays
  // saved on its background run), but confirm first so an accidental click
  // doesn't make the current review appear to vanish.
  const [switchPrompt, setSwitchPrompt] = useState<{
    jobId: string;
    unwritten: number;
  } | null>(null);
  const viewRunResults = (jobId: string) => {
    const job = jobs.getJob(jobId);
    if (!job?.resultItems) return;
    const switchingRuns =
      reviewItems.length > 0 &&
      !!reviewExecutionId &&
      job.executionId !== reviewExecutionId;
    if (switchingRuns) {
      const unwritten = reviewItems.length - (reviewWrittenKeys?.length ?? 0);
      if (unwritten > 0) {
        setSwitchPrompt({ jobId, unwritten });
        return;
      }
    }
    openRunResults(jobId);
  };

  // When the dialog-focused run finishes, auto-open its results (completed) or
  // drop back to the tray (failed/cancelled) so the dialog never sticks open.
  useEffect(() => {
    if (!focusedJob) return;
    if (focusedJob.status === "completed" && focusedJob.resultItems) {
      openRunResults(focusedJob.id);
    } else if (
      focusedJob.status === "failed" ||
      focusedJob.status === "cancelled"
    ) {
      setFocusedJobId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedJob?.status]);

  // Notify when a background ingest run finishes — even if it wasn't the focused
  // one — so the user knows there's still a review-and-write step to do.
  const [jobToast, setJobToast] = useState<{
    id: string;
    label: string;
    status: "completed" | "failed";
    resultCount?: number;
    skipped: string[];
  } | null>(null);
  const notifiedJobsRef = useRef<Set<string>>(new Set());
  const jobToastSeededRef = useRef(false);
  useEffect(() => {
    const terminal = jobs.jobs.filter(
      (j) =>
        !isRetryJob(j) && (j.status === "completed" || j.status === "failed")
    );
    // First pass: seed with already-finished jobs (from a prior session) so we
    // only toast runs that finish during THIS session.
    if (!jobToastSeededRef.current) {
      jobToastSeededRef.current = true;
      terminal.forEach((j) => notifiedJobsRef.current.add(j.id));
      return;
    }
    for (const j of terminal) {
      if (notifiedJobsRef.current.has(j.id)) continue;
      notifiedJobsRef.current.add(j.id);
      setJobToast({
        id: j.id,
        label: j.label,
        status: j.status as "completed" | "failed",
        resultCount: j.resultCount,
        skipped: (j.excluded || []).map((e) => e.raw_input),
      });
    }
  }, [jobs.jobs]);

  const [cfg, setCfg] = useState(() => chainIdConfig());
  const [ingestDraft, setIngestDraft] = useState(cfg.ingest.value);
  const [mergeDraft, setMergeDraft] = useState(cfg.merge.value);
  const [altFinderDraft, setAltFinderDraft] = useState(cfg.altFinder.value);
  const [autoMergeDraft, setAutoMergeDraft] = useState(cfg.autoMerge);

  // Server-side config: chain ids, staff status, review rules, pricing and
  // the caller's balance. Chain ids resolve through it once loaded.
  const [serverCfg, setServerCfg] = useState<CatalogConfig | null>(null);
  const [serverCfgLoaded, setServerCfgLoaded] = useState(false);
  const refreshServerConfig = useCallback(async () => {
    try {
      const next = await api.getCatalogConfig();
      setServerCatalogConfig(next);
      setServerCfg(next);
      const fresh = chainIdConfig();
      setCfg(fresh);
      setIngestDraft(fresh.ingest.value);
      setMergeDraft(fresh.merge.value);
      setAltFinderDraft(fresh.altFinder.value);
    } catch {
      setServerCatalogConfig(null);
      setServerCfg(null);
    } finally {
      setServerCfgLoaded(true);
    }
  }, [api]);
  // Reload on mount and whenever a run finishes, so the balance stays current.
  const finishedRunCount = jobs.jobs.filter(
    (j) => j.status === "completed" || j.status === "failed"
  ).length;
  useEffect(() => {
    void refreshServerConfig();
  }, [refreshServerConfig, finishedRunCount]);
  const isStaff = serverCfg?.isStaff === true;
  const editsNeedReview = serverCfg
    ? serverCfg.editReviewRequired && !serverCfg.isStaff
    : true;

  const ingestChainId = cfg.ingest.value;
  const mergeChainId = cfg.merge.value;
  const altFinderChainId = cfg.altFinder.value;
  const autoMerge = cfg.autoMerge;

  const saveSettings = () => {
    setIngestChainIdOverride(ingestDraft);
    setMergeChainIdOverride(mergeDraft);
    setAltFinderChainIdOverride(altFinderDraft);
    setAutoMergeDuplicates(autoMergeDraft);
    setCfg(chainIdConfig());
    setSettingsOpen(false);
  };

  // Wipe any per-browser chain-id overrides and reload the env/default values
  // into the draft inputs (so a stray local override can be reset to whatever
  // the deployed env — or the built-in default — provides).
  const resetToEnv = () => {
    resetChainIdsToEnv();
    const fresh = chainIdConfig();
    setIngestDraft(fresh.ingest.envDefault);
    setMergeDraft(fresh.merge.envDefault);
    setAltFinderDraft(fresh.altFinder.envDefault);
    setCfg(fresh);
  };

  // Rough token estimate for researching `count` products (0 when unknown or
  // when the caller isn't charged).
  const estimateIngestTokens = (count: number): number =>
    isStaff
      ? 0
      : Math.max(
          0,
          Math.round((serverCfg?.tokensPerItem?.ingest || 0) * count)
        );

  // Only interrupt when the balance looks too low for the run; otherwise the
  // inline estimate next to the button is enough.
  const [costPrompt, setCostPrompt] = useState<{
    count: number;
    estimate: number;
    remaining: number;
    resolve: (ok: boolean) => void;
  } | null>(null);
  const confirmCost = (count: number, estimate: number): Promise<boolean> => {
    const remaining = serverCfg?.budget?.remaining;
    if (estimate <= 0 || typeof remaining !== "number" || remaining >= estimate)
      return Promise.resolve(true);
    return new Promise((resolve) =>
      setCostPrompt({ count, estimate, remaining, resolve })
    );
  };
  const closeCostPrompt = (ok: boolean) => {
    costPrompt?.resolve(ok);
    setCostPrompt(null);
  };

  // Start a background research run and pop its live dialog (which is hideable).
  const submitIngest = async (foodInput: string, productCount: number) => {
    const count = Math.max(1, productCount);
    if (!(await confirmCost(count, estimateIngestTokens(count))))
      throw new RunCancelledError();
    const jobId = await jobs.submit(ingestChainId, foodInput, productCount);
    setFocusedJobId(jobId);
  };

  const startNewBatch = () => {
    setReviewItems([]);
    setReviewExcluded([]);
    setReviewExecutionId(undefined);
    setStep("input");
  };

  const focusedInFlight =
    !!focusedJob &&
    (focusedJob.status === "running" || focusedJob.status === "starting");

  const missingChains = !ingestChainId || !mergeChainId;
  const hasReview = reviewItems.length > 0 || reviewExcluded.length > 0;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Catalog Manager</h2>
          <p className="text-sm text-gray-600">
            Add new branded foods to the Passio database, or search and update
            the ones already there.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {serverCfg?.budget && !isStaff && (
            <span className="text-xs text-gray-500">
              Balance:{" "}
              <span className="font-semibold text-gray-800">
                {Math.max(0, serverCfg.budget.remaining).toLocaleString()}
              </span>{" "}
              tokens ·{" "}
              <a
                href={TOP_UP_HREF}
                className="font-semibold text-indigo-700 hover:underline"
              >
                Top up
              </a>
            </span>
          )}
          {isStaff && (
            <button
              type="button"
              onClick={() => setSettingsOpen((o) => !o)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              title="Chain settings (Passio staff only)"
            >
              ⚙ Settings
            </button>
          )}
        </div>
      </div>

      {settingsOpen && isStaff && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h3 className="text-sm font-semibold text-gray-800">
            Chain configuration
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            Override the AI Studio chain IDs used by this tool. Precedence: this
            browser&apos;s saved value &gt; the deployed env var (Duplo secret)
            &gt; a built-in default. Leave a field blank to fall back to the env
            var / default. Use &quot;Reset to env defaults&quot; to wipe local
            overrides. Grocery replacement does not take a chain ID here — it
            uses the same LaunchDarkly value as the SDK route.
          </p>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-gray-600">
                Ingest pipeline chain ID
              </label>
              <input
                value={ingestDraft}
                onChange={(e) => setIngestDraft(e.target.value)}
                placeholder={cfg.ingest.envDefault || "uuid"}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs focus:border-indigo-500 focus:outline-none"
              />
              <span className="text-[11px] text-gray-400">
                {cfg.ingest.fromEnv ? "from env var" : "built-in default"}:{" "}
                {cfg.ingest.envDefault}
              </span>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600">
                Compare/merge chain ID
              </label>
              <input
                value={mergeDraft}
                onChange={(e) => setMergeDraft(e.target.value)}
                placeholder={cfg.merge.envDefault || "uuid"}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs focus:border-indigo-500 focus:outline-none"
              />
              <span className="text-[11px] text-gray-400">
                {cfg.merge.fromEnv ? "from env var" : "built-in default"}:{" "}
                {cfg.merge.envDefault}
              </span>
            </div>
            {groceryReplaceEnabled && (
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600">
                  Web product finder chain ID
                </label>
                <input
                  value={altFinderDraft}
                  onChange={(e) => setAltFinderDraft(e.target.value)}
                  placeholder={cfg.altFinder.envDefault || "uuid"}
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs focus:border-indigo-500 focus:outline-none"
                />
                <span className="text-[11px] text-gray-400">
                  {cfg.altFinder.fromEnv ? "from env var" : "built-in default"}:{" "}
                  {cfg.altFinder.envDefault} · used by &quot;Find more
                  options&quot; to web-search keto/UPF alternatives for items
                  with no suggestions
                </span>
              </div>
            )}
          </div>
          <label className="mt-4 flex cursor-pointer items-start gap-2.5">
            <input
              type="checkbox"
              checked={autoMergeDraft}
              onChange={(e) => setAutoMergeDraft(e.target.checked)}
              className="mt-0.5 h-4 w-4 cursor-pointer"
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-800">
                Auto-run AI merge for duplicates
              </span>
              <span className="block text-xs text-gray-500">
                When the review page loads, immediately compare each duplicate
                against its existing record so the merged result is ready to
                review. Turn off to run merges manually.
              </span>
            </span>
          </label>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={saveSettings}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              Save
            </button>
            <SecondaryButton type="button" onClick={resetToEnv}>
              Reset to env defaults
            </SecondaryButton>
            <SecondaryButton
              type="button"
              onClick={() => setSettingsOpen(false)}
            >
              Cancel
            </SecondaryButton>
          </div>
        </div>
      )}

      {missingChains && serverCfgLoaded && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {isStaff ? (
            <>
              One or more chain IDs are not configured.{" "}
              {!ingestChainId && <span>Set the ingest chain ID. </span>}
              {!mergeChainId && <span>Set the merge chain ID. </span>}
              Open <span className="font-semibold">⚙ Settings</span> to add
              them.
            </>
          ) : (
            <>
              Product research isn&apos;t available right now. You can still
              search and edit existing products. If this keeps happening,
              contact Passio support.
            </>
          )}
        </div>
      )}

      {/* Mode switch: the guided "add" flow vs. maintaining existing items. */}
      <div className="mt-5 flex flex-wrap gap-2">
        <ModeButton active={mode === "add"} onClick={() => setMode("add")}>
          ➕ Add new products
        </ModeButton>
        <ModeButton
          active={mode === "manage"}
          onClick={() => setMode("manage")}
        >
          🔎 Search &amp; edit existing
        </ModeButton>
        <ModeButton active={mode === "mine"} onClick={() => setMode("mine")}>
          📂 Your products
        </ModeButton>
        {groceryReplaceEnabled && (
          <ModeButton
            active={mode === "replace"}
            onClick={() => setMode("replace")}
          >
            🥑 Grocery Replacement Test
          </ModeButton>
        )}
      </div>

      {mode === "add" ? (
        <div className="mt-5">
          {/* Plain-English overview so anyone knows what happens. */}
          <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="text-sm font-semibold text-gray-800">
              How it works
            </div>
            <ol className="mt-2 grid grid-cols-1 gap-3 text-xs text-gray-600 md:grid-cols-3">
              <li>
                <span className="font-semibold text-gray-800">
                  1. Add your products.
                </span>{" "}
                Paste names, UPCs or links. We research and fact-check each one
                online
                {serverCfg && serverCfg.tokensPerItem.ingest > 0
                  ? ` (about ${Math.round(
                      serverCfg.tokensPerItem.ingest
                    ).toLocaleString()} tokens per product)`
                  : ""}
                .
              </li>
              <li>
                <span className="font-semibold text-gray-800">
                  2. Review the results.
                </span>{" "}
                Products already in the catalog are combined with your research
                automatically. Check and tweak anything; nothing is saved until
                you click Save.
              </li>
              <li>
                <span className="font-semibold text-gray-800">
                  3. Save to the catalog.
                </span>{" "}
                New products go live right away.
                {editsNeedReview
                  ? " Changes to products someone else added are sent to Passio for a quick review first."
                  : ""}{" "}
                Track everything under &ldquo;Your products&rdquo;.
              </li>
            </ol>
            {serverCfg && !isStaff && serverCfg.dailyCreateLimit > 0 && (
              <div className="mt-2 text-[11px] text-gray-500">
                New products added today:{" "}
                {serverCfg.createsToday.toLocaleString()} of{" "}
                {serverCfg.dailyCreateLimit.toLocaleString()} daily limit.
              </div>
            )}
          </div>

          {/* Step rail */}
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-stretch">
            <StepPill
              index={1}
              label="Add products"
              hint="Names, UPCs, or links"
              state={step === "input" ? "active" : "done"}
              enabled
              onClick={() => setStep("input")}
            />
            <StepPill
              index={2}
              label="Review & save"
              hint={
                !hasReview
                  ? "Run step 1 first"
                  : reviewItems.length > 0
                  ? `${reviewItems.length} ready to review${
                      reviewExcluded.length > 0
                        ? ` · ${reviewExcluded.length} skipped`
                        : ""
                    }`
                  : `${reviewExcluded.length} skipped — see why`
              }
              state={step === "review" ? "active" : hasReview ? "done" : "todo"}
              enabled={hasReview}
              onClick={() => hasReview && setStep("review")}
            />
          </div>

          <div className="mt-6">
            <div className={step === "input" ? "block" : "hidden"}>
              <IngestTab
                ingestChainId={ingestChainId}
                onSubmit={submitIngest}
                activeCount={jobs.activeCount}
                tokensPerProduct={
                  serverCfg?.tokensPerItem?.ingest || ROUGH_TOKENS_PER_PRODUCT
                }
                isStaff={isStaff}
                prefill={ingestPrefill}
              />
            </div>
            <div className={step === "review" ? "block" : "hidden"}>
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  Review &amp; save to the catalog
                </h3>
                <button
                  type="button"
                  onClick={startNewBatch}
                  className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm hover:border-indigo-300 hover:bg-indigo-100"
                >
                  <span aria-hidden="true">←</span> Add more products
                </button>
              </div>
              {reviewExcluded.length > 0 && (
                <SkippedInputsPanel
                  excluded={reviewExcluded}
                  foundCount={reviewItems.length}
                  onRetry={retryWithBrand}
                  onSearch={searchCatalogFor}
                />
              )}
              {reviewItems.length === 0 &&
                reviewExcluded.length === 0 &&
                reviewExecutionId && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
                    <div className="text-sm font-semibold text-gray-900">
                      This run didn&apos;t find any products
                    </div>
                    <p className="mx-auto mt-1 max-w-xl text-sm text-gray-600">
                      Research couldn&apos;t confirm a real product for what was
                      entered, so there&apos;s nothing to save. Try again with
                      the brand and full product name, a UPC/barcode, or a link
                      to the product page.
                    </p>
                    <div className="mt-4 flex justify-center">
                      <SecondaryButton type="button" onClick={startNewBatch}>
                        ← Try different products
                      </SecondaryButton>
                    </div>
                  </div>
                )}
              {reviewItems.length > 0 || !reviewExecutionId ? (
                <ReviewTab
                  api={api}
                  mergeChainId={mergeChainId}
                  reviewItems={reviewItems}
                  executionId={reviewExecutionId}
                  customerName={customerName}
                  autoMerge={autoMerge}
                  editsNeedReview={editsNeedReview}
                  writtenKeys={reviewWrittenKeys}
                  onResearchMore={startNewBatch}
                  onViewProducts={() => setMode("mine")}
                  onItemsWritten={(written) => {
                    void refreshServerConfig();
                    if (reviewExecutionId)
                      jobs.recordWrites(
                        reviewExecutionId,
                        written.map((w) => w.key)
                      );
                  }}
                  retry={
                    ingestChainId
                      ? {
                          // Use a stable origin id across start/filter/persist so a
                          // momentarily-undefined executionId can't hide the button
                          // or orphan an in-flight retry.
                          start: (foodInput, label, itemIndex) =>
                            jobs.retryItem(
                              ingestChainId,
                              foodInput,
                              label,
                              reviewExecutionId ?? "",
                              itemIndex
                            ),
                          jobs: jobs.jobs.filter(
                            (j) =>
                              isRetryJob(j) &&
                              j.retryForExecutionId ===
                                (reviewExecutionId ?? "")
                          ),
                          clear: jobs.clear,
                          persist: (itemIndex, newItem) =>
                            jobs.replaceRunItem(
                              reviewExecutionId ?? "",
                              itemIndex,
                              newItem
                            ),
                        }
                      : undefined
                  }
                />
              ) : null}

              {/* Repeat the "back to step 1" affordance at the bottom so it's
                  easy to follow the flow after scrolling the review list. */}
              <div className="mt-6 flex flex-col items-center gap-2 border-t border-gray-100 pt-5">
                <p className="text-sm text-gray-500">
                  Done here? Head back to step 1 to research more products.
                </p>
                <button
                  type="button"
                  onClick={startNewBatch}
                  className="inline-flex items-center gap-1.5 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm hover:border-indigo-300 hover:bg-indigo-100"
                >
                  <span aria-hidden="true">←</span> Add more products
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : mode === "manage" ? (
        <div className="mt-6">
          <SearchEditTab
            api={api}
            ingestChainId={ingestChainId}
            customerName={customerName}
            onSubmit={submitIngest}
            activeCount={jobs.activeCount}
            initialQuery={searchPrefill}
          />
        </div>
      ) : mode === "replace" && groceryReplaceEnabled ? (
        <div className="mt-6">
          <GroceryReplacementTab
            api={api}
            altFinderChainId={altFinderChainId}
            ingestConfigured={!!ingestChainId}
            onIngest={submitIngest}
          />
        </div>
      ) : (
        <div className="mt-6">
          <ContributionsTab
            api={api}
            ingestChainId={ingestChainId}
            customerName={customerName}
          />
        </div>
      )}

      {/* Live progress dialog for the focused background run (hideable). */}
      <ChainRunOverlay
        open={focusedInFlight}
        progress={focusedJob?.progress || null}
        credits={focusedJob?.credits}
        title={focusedJob ? `Researching: ${focusedJob.label}` : undefined}
        onHide={() => setFocusedJobId(null)}
        onCancel={() => {
          if (focusedJobId) jobs.cancel(focusedJobId);
          setFocusedJobId(null);
        }}
      />

      <Modal
        open={!!costPrompt}
        onClose={() => closeCostPrompt(false)}
        title="Your balance may not cover this run"
        footer={
          <>
            <SecondaryButton
              type="button"
              onClick={() => closeCostPrompt(false)}
            >
              Cancel
            </SecondaryButton>
            <SecondaryButton
              type="button"
              onClick={() => closeCostPrompt(true)}
            >
              Run anyway
            </SecondaryButton>
            <a
              href={TOP_UP_HREF}
              onClick={() => closeCostPrompt(false)}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
            >
              Top up balance
            </a>
          </>
        }
      >
        {costPrompt && (
          <div className="space-y-3 text-sm text-gray-700">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="text-xs text-gray-500">
                  Estimated for {costPrompt.count} product
                  {costPrompt.count === 1 ? "" : "s"}
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  ~{costPrompt.estimate.toLocaleString()}
                  <span className="ml-1 text-xs font-normal text-gray-500">
                    tokens
                  </span>
                </div>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <div className="text-xs text-amber-700">Your balance</div>
                <div className="text-lg font-semibold text-amber-900">
                  {Math.max(0, costPrompt.remaining).toLocaleString()}
                  <span className="ml-1 text-xs font-normal text-amber-700">
                    tokens
                  </span>
                </div>
              </div>
            </div>
            <p>
              You can still run it. You&apos;re only charged for what the run
              actually uses, and once your balance runs out new runs pause until
              you top up.
            </p>
          </div>
        )}
      </Modal>

      <Modal
        open={!!switchPrompt}
        onClose={() => setSwitchPrompt(null)}
        title="Switch to this run's results?"
        footer={
          <>
            <SecondaryButton
              type="button"
              onClick={() => setSwitchPrompt(null)}
            >
              Keep reviewing
            </SecondaryButton>
            <button
              type="button"
              onClick={() => {
                if (switchPrompt) openRunResults(switchPrompt.jobId);
                setSwitchPrompt(null);
              }}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
            >
              Switch
            </button>
          </>
        }
      >
        {switchPrompt && (
          <p className="text-sm text-gray-700">
            You still have {switchPrompt.unwritten} product
            {switchPrompt.unwritten === 1 ? "" : "s"} to review from your
            current run. Nothing is lost: that run stays under{" "}
            <span className="font-semibold">Background runs</span> and you can
            reopen it anytime.
          </p>
        )}
      </Modal>

      {/* Persistent tray of active + recent runs (the "alert area"). */}
      <IngestJobsTray
        jobs={jobs.jobs.filter((j) => !isRetryJob(j))}
        activeCount={jobs.activeCount}
        onOpen={setFocusedJobId}
        onView={viewRunResults}
        onCancel={jobs.cancel}
        onClear={jobs.clear}
        onClearFinished={jobs.clearFinished}
        transientRuns={backgroundRuns.runs}
      />

      {/* Completion nudge for background ingest runs — reminds the user there's
          still a review + write step (a common "I thought I was done" trap). */}
      <Toast
        open={!!jobToast}
        tone={
          jobToast?.status === "failed" || !jobToast?.resultCount
            ? "warn"
            : "success"
        }
        message={jobToastMessage(jobToast)}
        actionLabel={
          jobToast?.status !== "completed"
            ? undefined
            : jobToast.resultCount
            ? "Review & save →"
            : "See why →"
        }
        onAction={
          jobToast?.status === "completed"
            ? () => {
                if (jobToast) viewRunResults(jobToast.id);
                setJobToast(null);
              }
            : undefined
        }
        onClose={() => setJobToast(null)}
        duration={0}
      />
    </div>
  );
};

// Wrap in the background-runs provider so every backgroundable action (ingest,
// grocery replacement, sourcing finder) surfaces in the one shared runs tray.
const CatalogManager: React.FC<CatalogManagerProps> = (props) => (
  <BackgroundRunsProvider>
    <CatalogManagerInner {...props} />
  </BackgroundRunsProvider>
);

export default CatalogManager;
